import { type FC, type ReactElement, useContext, useState } from "react";
import { Navigate } from "react-router-dom";

import { useMutation } from "@apollo/client";
import {
    Button,
    Card,
    Colors,
    Control,
    DropdownInput,
    Input,
    Option,
    Tag,
    TagType,
    TooltipIndicator,
} from "@snapchat/snap-design-system";
import { CheckIcon, ErrorIcon, InformationIcon, LockedIcon, Sizes } from "@snapchat/snap-design-system-icons";
import { useFormik } from "formik";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import { AssetType, MyLensesAssetCategory } from "../../gql-generated/types";
import { PageHeader, PageHeaderContainer } from "../../layout/components";
import { UserInfoContext } from "../../shared/api/contexts";
import { ASSET_LIBRARY_GUIDELINES_URL } from "../../shared/constants";
import { usePageRedirect, useSelectedOrganizationContainer } from "../../shared/hooks";
import { Feature } from "../../shared/models";
import { submitAsset } from "../api/mutations/submitAsset";
import {
    type SubmitAssetMutation,
    type SubmitAssetMutationVariables,
} from "../api/mutations/submitAsset.gql-generated";
import { getOrganizationAssets } from "../api/queries/getOrganizationAssets";
import { assetCategoryToDisplayText } from "../utils/assetCategory";
import { formatSize } from "../utils/formatSize";

import { AssetPreviewCard } from "./AssetPreviewCard";
import {
    CardContent,
    CategoryPills,
    CreateAssetContainer,
    DerivedCaption,
    DerivedChip,
    ErrorSummary,
    ErrorSummaryList,
    ErrorSummaryTitle as ErrorSummaryTitleRow,
    ErrorWrapper,
    FormColumn,
    FormLayout,
    InfoCallout,
    InfoCalloutText,
    PreviewColumn,
    ProgressDonut,
    ProgressDonutLabel,
    StatusCard,
    StatusHeading,
    StatusSubHeading,
    StatusText,
    StyledCard,
    SubmitRow,
    TypeHintList,
    UploadsRow,
    requiredCss,
    selectedChipCss,
} from "./AssetSubmission.styles";
import { AssetsBreadcrumb } from "./AssetsBreadcrumb";
import { FileDropZone } from "./FileDropZone";
import { RichTextEditor } from "./RichTextEditor";
import {
    Asset,
    AssetDropHint,
    AssetErrorDefault,
    AssetErrorFileTooLarge,
    AssetErrorFileType,
    AssetErrorUploadFailed,
    AssetInvalidType,
    AssetLibraryNote,
    AssetLibrarySubtext,
    AssetRequired,
    AssetTooLarge,
    Category,
    CategoryRequired,
    DerivedCategoryCaption,
    Description,
    DescriptionRequired,
    ErrorSummaryTitle,
    Name,
    NameRequired,
    NewAssetTitle,
    RequiredFieldsMissing,
    StatusIncompleteSubtitle,
    StatusReadySubtitle,
    StatusReadyTitle,
    StatusSubmittedSubtitle,
    StatusSubmittedTitle,
    Submit,
    SubmitAnother,
    Thumbnail,
    ThumbnailDropHint,
    ThumbnailHint,
    ThumbnailInvalidType,
    ThumbnailRequired,
    ThumbnailTooLarge,
    TypeAssetPackage,
    TypeCustomComponent,
    TypeHintAssetPackage,
    TypeHintCustomComponent,
    TypeHintPlugin,
    Type as TypeLabel,
    TypePlugin,
} from "./messages.generated";

// `type` drives the accepted asset formats and is submitted as the asset's AssetType.
const ASSET_TYPE_OPTIONS = [
    {
        value: "Asset Package",
        label: <TypeAssetPackage />,
        Hint: TypeHintAssetPackage,
        assetType: AssetType.AssetPackage,
    },
    {
        value: "Custom Component",
        label: <TypeCustomComponent />,
        Hint: TypeHintCustomComponent,
        assetType: AssetType.CustomComponent,
    },
    { value: "Plugin", label: <TypePlugin />, Hint: TypeHintPlugin, assetType: AssetType.Plugin },
];

// The form no longer collects Lens Studio version bounds; the API requires them,
// so submit sentinels: a floor min and an unbounded max meaning "supported on all
// newer Lens Studio versions".
const DEFAULT_MIN_LENS_STUDIO_VERSION = "5.20.0";
const DEFAULT_MAX_LENS_STUDIO_VERSION = "99.0.0";

const ASSET_MAX_BYTES = 100 * 1024 * 1024;
const THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;
const THUMBNAIL_MIME_TYPES = new Set(["image/png"]);

const PLUGIN_ASSET_EXTENSIONS = [".zip"];
const DEFAULT_ASSET_EXTENSIONS = [".lspkg"];
const CUSTOM_COMPONENT_ASSET_EXTENSIONS = [".lsc"];
const ASSET_PACKAGE_ASSET_EXTENSIONS = [".lspkg"];
const THUMBNAIL_EXTENSIONS = [".png"];

const ASSET_EXTENSIONS_BY_TYPE: Record<string, string[]> = {
    Plugin: PLUGIN_ASSET_EXTENSIONS,
    "Custom Component": CUSTOM_COMPONENT_ASSET_EXTENSIONS,
    "Asset Package": ASSET_PACKAGE_ASSET_EXTENSIONS,
    Default: DEFAULT_ASSET_EXTENSIONS,
};

const getAssetExtensions = (type: string | undefined): string[] =>
    ASSET_EXTENSIONS_BY_TYPE[type ?? ""] ?? DEFAULT_ASSET_EXTENSIONS;

const matchesExtension = (file: File, extensions: string[]): boolean =>
    extensions.some((ext) => file.name.toLowerCase().endsWith(ext));

// Validates an asset file against the accepted extensions for the given Type + size limit.
// Returns an error message, or null when valid. Used on upload and re-run when Type changes.
const validateAssetFile = (file: File, type: string): ReactElement | null => {
    const extensions = getAssetExtensions(type);
    if (!matchesExtension(file, extensions)) {
        return <AssetInvalidType fileName={file.name} formats={extensions.join(", ")} />;
    }
    if (file.size > ASSET_MAX_BYTES) {
        return <AssetTooLarge fileName={file.name} size={formatSize(file.size)} />;
    }
    return null;
};

// Some backend categories duplicate a Type value. When a Type is a key here, the
// matching category is derived automatically (shown as a locked "from Type" chip)
// and is not offered as a free chip — so the two fields can never contradict.
const DERIVED_TYPE_CATEGORY: Record<string, MyLensesAssetCategory> = {
    Plugin: MyLensesAssetCategory.Plugin,
    "Custom Component": MyLensesAssetCategory.CustomComponents,
};

const isDerivedType = (type: string | undefined): boolean => Boolean(type && type in DERIVED_TYPE_CATEGORY);

// Creator-selectable chips. Plugins / Custom Components are derived from Type (above);
// Featured / Essentials / Music / APIs are backend-owned; Script Modules is omitted for now.
const CATEGORY_OPTIONS: MyLensesAssetCategory[] = [
    MyLensesAssetCategory.TwoDimensional,
    MyLensesAssetCategory.ThreeDimensional,
    MyLensesAssetCategory.Materials,
    MyLensesAssetCategory.Tools,
    MyLensesAssetCategory.Vfx,
    MyLensesAssetCategory.Snapml,
    MyLensesAssetCategory.Audio,
    MyLensesAssetCategory.Reference,
    MyLensesAssetCategory.Spectacles,
    MyLensesAssetCategory.Ui,
    MyLensesAssetCategory.Games,
];

// Submission runs the whole validate/upload/publish pipeline synchronously
// server-side, so it can outlive the global 60s Apollo timeout on large files.
const SUBMIT_TIMEOUT_MS = 5 * 60_000;

const STATUS_ACCENT_INCOMPLETE = Colors.Yellow.V100;
// No SDS token matches this design-handoff green.
const STATUS_ACCENT_COMPLETE = "#39CA8E";

// Messages are rendered from the `requirements` list below, not from Yup, so
// these only need to make the fields fail validation.
const AssetSubmissionSchema = Yup.object().shape({
    asset: Yup.mixed<File>()
        .required("required")
        .test("file", "required", (value) => value instanceof File && !!value.name),
    thumbnail: Yup.mixed<File>()
        .required("required")
        .test("file", "required", (value) => value instanceof File && !!value.name),
    name: Yup.string().required("required"),
    description: Yup.string().required("required"),
    categories: Yup.array().test("required", "required", (value, context) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument -- sibling form value
        return (value?.length ?? 0) > 0 || isDerivedType(context.parent?.type);
    }),
    minLensStudioVersion: Yup.string().nullable(),
    maxLensStudioVersion: Yup.string().nullable(),
});

export const AssetSubmission: FC = () => {
    const redirect = usePageRedirect(Feature.assetsCreate);
    const { orgId, organizationName } = useSelectedOrganizationContainer();

    const [submitNewAsset, { loading, error }] = useMutation<SubmitAssetMutation, SubmitAssetMutationVariables>(
        submitAsset,
        {
            context: { timeout: SUBMIT_TIMEOUT_MS },
            refetchQueries: [
                {
                    query: getOrganizationAssets,
                    variables: {
                        organizationId: orgId,
                        limit: 1000,
                    },
                },
            ],
        },
    );

    const [assetError, setAssetError] = useState<ReactElement | null>(null);
    const [thumbError, setThumbError] = useState<ReactElement | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [submitAttempt, setSubmitAttempt] = useState(0);

    const userInfo = useContext(UserInfoContext);
    const intl = useIntl();
    // Quill's placeholder must be a plain string, so it can't use a <FormattedMessage/>.
    const descriptionPlaceholder = intl.formatMessage({
        id: "apps.assets.components.descriptionPlaceholder",
        defaultMessage: "Describe your asset — what it does and how to use it.",
    });
    const { submitForm, isSubmitting, values, touched, setFieldTouched, setFieldValue, errors, resetForm } = useFormik({
        initialValues: {
            asset: {} as File,
            thumbnail: {} as File,
            name: "",
            type: "Asset Package",
            description: "",
            categories: [] as MyLensesAssetCategory[],
            minLensStudioVersion: "",
            maxLensStudioVersion: "",
        },
        isInitialValid: false,
        validateOnBlur: true,
        validationSchema: AssetSubmissionSchema,
        onSubmit: async (formValues) => {
            const derived = DERIVED_TYPE_CATEGORY[formValues.type];
            const effectiveCategories = derived ? [derived, ...formValues.categories] : formValues.categories;
            const assetType =
                ASSET_TYPE_OPTIONS.find((option) => option.value === formValues.type)?.assetType ?? AssetType.Default;

            try {
                const submitRes = await submitNewAsset({
                    variables: {
                        input: {
                            creatorAlias: userInfo?.data?.getUserProfile.userDisplayName ?? "",
                            organizationId: orgId ?? "",
                            organizationName,
                            asset: formValues.asset,
                            thumbnail: formValues.thumbnail,
                            name: formValues.name,
                            description: formValues.description,
                            categories: effectiveCategories,
                            minLensStudioVersion: formValues.minLensStudioVersion || DEFAULT_MIN_LENS_STUDIO_VERSION,
                            maxLensStudioVersion: formValues.maxLensStudioVersion || DEFAULT_MAX_LENS_STUDIO_VERSION,
                            type: assetType,
                        },
                    },
                });

                if (submitRes.errors === undefined) {
                    setSubmitted(true);
                }
            } catch (submitError: unknown) {
                console.error(submitError);
            }
        },
    });

    const handleError = () => {
        if (!error) return "";
        const { message, graphQLErrors } = error;
        switch (true) {
            case graphQLErrors[0]?.extensions["code"] === "ASSET_UPLOAD_FAILED":
                return <AssetErrorUploadFailed />;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- it exists in the error
            case Boolean(graphQLErrors[0]?.extensions["response"]?.body):
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- it exists it exists in the error
                return graphQLErrors[0]?.extensions["response"]?.body as string;
            case message.includes("Cannot create a string longer than"):
                return <AssetErrorFileTooLarge />;
            case message.includes("is not a valid file"):
                return <AssetErrorFileType />;
            default:
                return <AssetErrorDefault />;
        }
    };

    if (redirect) {
        return <Navigate to={redirect} />;
    }

    const handleCustomChange = async (name: string, value?: string | string[] | File) => {
        await setFieldValue(name, value);
        void setFieldTouched(name, true);
    };

    const toggleCategory = (category: MyLensesAssetCategory) => {
        const next = values.categories.includes(category)
            ? values.categories.filter((current) => current !== category)
            : [...values.categories, category];
        void handleCustomChange("categories", next);
    };

    const handleAssetSelect = (file: File) => {
        const validationError = validateAssetFile(file, values.type);
        if (validationError) {
            setAssetError(validationError);
            // Clear any previously-selected file so the UI matches the empty-prompt state.
            void handleCustomChange("asset", {} as File);
            return;
        }
        setAssetError(null);
        void handleCustomChange("asset", file);
    };

    const handleTypeChange = (type: string) => {
        void handleCustomChange("type", type);
        // The accepted asset formats depend on Type — re-check an already-uploaded file.
        if (values.asset.name) {
            setAssetError(validateAssetFile(values.asset, type));
        }
    };

    const handleThumbnailSelect = (file: File) => {
        if (!THUMBNAIL_MIME_TYPES.has(file.type) && !matchesExtension(file, THUMBNAIL_EXTENSIONS)) {
            setThumbError(<ThumbnailInvalidType fileName={file.name} />);
            void handleCustomChange("thumbnail", {} as File);
            return;
        }
        if (file.size > THUMBNAIL_MAX_BYTES) {
            setThumbError(<ThumbnailTooLarge fileName={file.name} size={formatSize(file.size)} />);
            void handleCustomChange("thumbnail", {} as File);
            return;
        }
        setThumbError(null);
        void handleCustomChange("thumbnail", file);
    };

    const handleAssetRemove = () => {
        setAssetError(null);
        void handleCustomChange("asset", {} as File);
    };

    const handleThumbnailRemove = () => {
        setThumbError(null);
        void handleCustomChange("thumbnail", {} as File);
    };

    const handleReset = () => {
        resetForm();
        setAssetError(null);
        setThumbError(null);
        setSubmitted(false);
        setSubmitAttempt(0);
    };

    const derivedCategory = DERIVED_TYPE_CATEGORY[values.type];

    // The five required fields, mirrored here to drive the progress donut + error summary.
    const requirements = [
        { key: "name", done: values.name.trim().length > 0, message: <NameRequired /> },
        {
            key: "categories",
            done: values.categories.length > 0 || Boolean(derivedCategory),
            message: <CategoryRequired />,
        },
        { key: "asset", done: Boolean(values.asset.name) && !assetError, message: assetError ?? <AssetRequired /> },
        {
            key: "thumbnail",
            done: Boolean(values.thumbnail.name) && !thumbError,
            message: thumbError ?? <ThumbnailRequired />,
        },
        { key: "description", done: values.description.trim().length > 0, message: <DescriptionRequired /> },
    ];
    const doneCount = requirements.filter((requirement) => requirement.done).length;
    const total = requirements.length;
    const ready = doneCount === total;
    const accent = ready || submitted ? STATUS_ACCENT_COMPLETE : STATUS_ACCENT_INCOMPLETE;
    const unmet = requirements.filter((requirement) => !requirement.done);
    const showErrorSummary = submitAttempt > 0 && !ready && !submitted;

    const handleSubmitClick = () => {
        (["name", "type", "categories", "asset", "thumbnail", "description"] as const).forEach((field) => {
            void setFieldTouched(field, true);
        });
        if (ready) {
            // throw to error boundary
            submitForm().catch((submitError) => {
                throw submitError;
            });
        } else {
            setSubmitAttempt((attempt) => attempt + 1);
        }
    };

    const assetAccept = getAssetExtensions(values.type).join(",");
    const AssetTypeHint = ASSET_TYPE_OPTIONS.find((option) => option.value === values.type)?.Hint;

    const submitButton = submitted ? (
        <Button type="primary" onClick={handleReset}>
            <SubmitAnother />
        </Button>
    ) : (
        <Button type={ready ? "primary" : "default"} onClick={handleSubmitClick} isPending={loading || isSubmitting}>
            <Submit />
        </Button>
    );

    let statusTitle = <RequiredFieldsMissing />;
    let statusSubtitle: ReactElement = <StatusIncompleteSubtitle count={total - doneCount} total={total} />;
    if (submitted) {
        statusTitle = <StatusSubmittedTitle />;
        statusSubtitle = <StatusSubmittedSubtitle />;
    } else if (ready) {
        statusTitle = <StatusReadyTitle />;
        statusSubtitle = <StatusReadySubtitle />;
    }

    return (
        <CreateAssetContainer>
            <AssetsBreadcrumb />
            <PageHeaderContainer>
                <PageHeader>
                    <NewAssetTitle />
                </PageHeader>
            </PageHeaderContainer>
            {error !== undefined && (
                <Card status="error" titleBorder={false}>
                    {handleError()}
                </Card>
            )}

            <StatusCard>
                <CardContent>
                    <ProgressDonut $accent={accent} $pct={Math.round((doneCount / total) * 100)}>
                        <ProgressDonutLabel>
                            {doneCount}/{total}
                        </ProgressDonutLabel>
                    </ProgressDonut>
                    <StatusText>
                        <StatusHeading>{statusTitle}</StatusHeading>
                        <StatusSubHeading>{statusSubtitle}</StatusSubHeading>
                    </StatusText>
                </CardContent>
                {submitButton}
            </StatusCard>

            {showErrorSummary && (
                <ErrorSummary key={submitAttempt}>
                    <ErrorSummaryTitleRow>
                        <ErrorIcon fill={Colors.Red.V200} size={Sizes.Medium} />
                        <ErrorSummaryTitle />
                    </ErrorSummaryTitleRow>
                    <ErrorSummaryList>
                        {unmet.map((requirement) => (
                            <li key={requirement.key}>{requirement.message}</li>
                        ))}
                    </ErrorSummaryList>
                </ErrorSummary>
            )}

            <StyledCard>
                <InfoCallout>
                    <InformationIcon fill={Colors.Blue.V200} size={Sizes.Medium} />
                    <InfoCalloutText>
                        <AssetLibraryNote />
                        <a href={ASSET_LIBRARY_GUIDELINES_URL} rel="noreferrer" target="_blank">
                            <AssetLibrarySubtext />
                        </a>
                    </InfoCalloutText>
                </InfoCallout>
                <FormLayout>
                    <FormColumn>
                        <Input
                            className={requiredCss}
                            label={<Name />}
                            name={"name"}
                            value={values.name}
                            onChange={(value: string) => void handleCustomChange("name", value)}
                            error={touched.name && !values.name.trim() ? <NameRequired /> : undefined}
                        />
                        <DropdownInput
                            className={requiredCss}
                            label={
                                <>
                                    <TypeLabel />
                                    <TooltipIndicator
                                        content={
                                            <TypeHintList>
                                                {ASSET_TYPE_OPTIONS.map(({ value, Hint }) => (
                                                    <li key={value}>
                                                        <Hint />
                                                    </li>
                                                ))}
                                            </TypeHintList>
                                        }
                                    />
                                </>
                            }
                            name={"type"}
                            value={values.type}
                            onChange={(value: string) => {
                                handleTypeChange(value);
                            }}
                        >
                            {ASSET_TYPE_OPTIONS.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </DropdownInput>
                        <Control label={<Category />} className={requiredCss}>
                            <CategoryPills>
                                {derivedCategory && (
                                    <DerivedChip>
                                        <LockedIcon size={Sizes.Small} />
                                        {assetCategoryToDisplayText(derivedCategory)}
                                    </DerivedChip>
                                )}
                                {CATEGORY_OPTIONS.map((category) => {
                                    const isSelected = values.categories.includes(category);
                                    return (
                                        <Tag
                                            key={category}
                                            type={TagType.OUTLINE}
                                            className={isSelected ? selectedChipCss : undefined}
                                            icon={isSelected ? CheckIcon : undefined}
                                            onClick={() => {
                                                toggleCategory(category);
                                            }}
                                        >
                                            {assetCategoryToDisplayText(category)}
                                        </Tag>
                                    );
                                })}
                            </CategoryPills>
                            {derivedCategory && (
                                <DerivedCaption>
                                    <DerivedCategoryCaption category={assetCategoryToDisplayText(derivedCategory)} />
                                </DerivedCaption>
                            )}
                            {touched.categories && !derivedCategory && values.categories.length === 0 && (
                                <ErrorWrapper>
                                    <ErrorIcon fill={Colors.Red.V200} />
                                    <span className="sds-control-status error">
                                        <CategoryRequired />
                                    </span>
                                </ErrorWrapper>
                            )}
                        </Control>
                        <UploadsRow>
                            <FileDropZone
                                className={requiredCss}
                                variant="asset"
                                label={
                                    <>
                                        <Asset />
                                        {AssetTypeHint && <TooltipIndicator content={<AssetTypeHint />} />}
                                    </>
                                }
                                hint={<AssetDropHint formats={getAssetExtensions(values.type).join(", ")} />}
                                name={"asset"}
                                accept={assetAccept}
                                file={values.asset}
                                onSelect={handleAssetSelect}
                                onRemove={handleAssetRemove}
                                error={assetError ?? (touched.asset && !values.asset.name ? <AssetRequired /> : null)}
                            />
                            <FileDropZone
                                className={requiredCss}
                                variant="thumbnail"
                                label={
                                    <>
                                        <Thumbnail />
                                        <TooltipIndicator
                                            content={
                                                <ThumbnailHint
                                                    guideLink={
                                                        <a
                                                            href={ASSET_LIBRARY_GUIDELINES_URL}
                                                            rel="noreferrer"
                                                            target="_blank"
                                                        >
                                                            <AssetLibrarySubtext />
                                                        </a>
                                                    }
                                                />
                                            }
                                        />
                                    </>
                                }
                                hint={<ThumbnailDropHint />}
                                name={"thumbnail"}
                                accept={THUMBNAIL_EXTENSIONS.join(",")}
                                file={values.thumbnail}
                                onSelect={handleThumbnailSelect}
                                onRemove={handleThumbnailRemove}
                                error={
                                    thumbError ??
                                    (touched.thumbnail && !values.thumbnail.name ? <ThumbnailRequired /> : null)
                                }
                            />
                        </UploadsRow>
                        <Control label={<Description />} className={requiredCss}>
                            <RichTextEditor
                                value={values.description}
                                placeholder={descriptionPlaceholder}
                                hasError={Boolean(touched.description && errors.description)}
                                onChange={(value: string) => void handleCustomChange("description", value)}
                                onBlur={() => void setFieldTouched("description", true)}
                            />
                            {touched.description && errors.description && (
                                <ErrorWrapper>
                                    <ErrorIcon fill={Colors.Red.V200} />
                                    <span className="sds-control-status error">
                                        <DescriptionRequired />
                                    </span>
                                </ErrorWrapper>
                            )}
                        </Control>
                    </FormColumn>
                    <PreviewColumn>
                        <AssetPreviewCard
                            name={values.name}
                            creator={userInfo?.data?.getUserProfile.userDisplayName}
                            image={values.thumbnail}
                            sizeBytes={values.asset.size}
                            description={values.description}
                        />
                    </PreviewColumn>
                </FormLayout>
            </StyledCard>

            <SubmitRow>{submitButton}</SubmitRow>
        </CreateAssetContainer>
    );
};
